import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { MODULES, moduleName } from "@/lib/modules";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BrandMark } from "@/components/BrandMark";
import {
  LayoutDashboard, ShieldAlert, Wallet, ScrollText, Settings as SettingsIcon,
  LogOut, ShoppingBag, Rocket, BookOpen, Sparkles,
} from "lucide-react";

export function AppShell() {
  const { user, logout } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-white/10 bg-[#080808] flex flex-col">
        <div className="px-5 pt-5 pb-4 border-b border-white/10">
          <NavLink to="/dashboard" className="flex items-center gap-2.5" data-testid="brand-link">
            <BrandMark size={28} />
            <div>
              <div className="font-display font-medium tracking-tight text-sm">EMRKT.CH</div>
              <div className="font-mono text-[10px] text-white/40 uppercase">operator</div>
            </div>
          </NavLink>
        </div>

        <nav className="p-3 space-y-1 overflow-y-auto flex-1">
          <NavItem to="/dashboard" testid="nav-overview" Icon={LayoutDashboard} label={t(lang, "sidebar_overview")} end />

          <div className="pt-4 pb-1 px-3 text-[10px] uppercase tracking-widest text-white/40 font-mono">
            {t(lang, "sidebar_modules")}
          </div>
          {MODULES.map((m) => (
            <NavItem
              key={m.id}
              to={`/dashboard/module/${m.id}`}
              testid={`nav-module-${m.id}`}
              Icon={m.Icon}
              label={moduleName(m.id, lang)}
            />
          ))}

          <div className="pt-4 pb-1 px-3 text-[10px] uppercase tracking-widest text-white/40 font-mono">
            System
          </div>
          <NavItem to="/dashboard/guardian" testid="nav-guardian" Icon={ShieldAlert} label={t(lang, "sidebar_guardian")} />
          <NavItem to="/dashboard/growth" testid="nav-growth" Icon={Rocket} label={t(lang, "sidebar_growth")} />
          <NavItem to="/dashboard/growth-ai" testid="nav-growth-ai" Icon={Sparkles} label="Growth AI" />
          <NavItem to="/dashboard/ebooks" testid="nav-ebooks" Icon={BookOpen} label={t(lang, "sidebar_ebooks")} />
          <NavItem to="/dashboard/finance" testid="nav-finance" Icon={Wallet} label={t(lang, "sidebar_finance")} />
          <NavItem to="/dashboard/shopify" testid="nav-shopify" Icon={ShoppingBag} label={t(lang, "sidebar_shopify")} />
          <NavItem to="/dashboard/audit" testid="nav-audit" Icon={ScrollText} label={t(lang, "sidebar_audit")} />
          <NavItem to="/dashboard/settings" testid="nav-settings" Icon={SettingsIcon} label={t(lang, "sidebar_settings")} />
        </nav>

        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-2 px-2 py-2">
            <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-xs font-mono">
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs truncate">{user?.name || user?.email}</div>
              <div className="text-[10px] font-mono uppercase text-white/40">{user?.role}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="text-white/50 hover:text-[color:var(--hq-swiss-red)] transition-colors duration-200 p-1"
              aria-label={t(lang, "sidebar_logout")}
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-white/10 bg-black/60 backdrop-blur-xl flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">
              Operator Console
            </span>
            <span className="hq-dot hq-dot-cyan"></span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-6 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function NavItem({ to, Icon, label, testid, end = false }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) => `hq-nav-item ${isActive ? "active" : ""}`}
    >
      <Icon size={14} />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}
